import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import Button from "@mui/material/Button";
import { ExpandLessRounded, ExpandMoreRounded, RefreshRounded, InfoOutlined as InfoOutlinedIcon, FilterAltRounded } from "@mui/icons-material";

import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { MRP_SHEET_API } from "../data";
import {
	tone,
	priceBandTones,
	ink,
	muted,
	line,
	panelBg,
	card,
	eyebrow,
	colHead,
	nf,
	inr,
	KpiTile,
	MiniBars,
	DimensionToggle,
	ScopeChips,
	splitAnalyticsFilters,
} from "components/PimDashboard/DashboardComponents/shared/analyticsUi";

const PRICE_BANDS = [
	{ key: "<1k", label: "< ₹1k", color: priceBandTones[0] },
	{ key: "1-2k", label: "₹1–2k", color: priceBandTones[1] },
	{ key: "2-3.5k", label: "₹2–3.5k", color: priceBandTones[2] },
	{ key: "3.5-6k", label: "₹3.5–6k", color: priceBandTones[3] },
	{ key: "6k+", label: "₹6k+", color: priceBandTones[4] },
];

const DIMENSIONS = [
	{ key: "brand", label: "Brand" },
	{ key: "season", label: "Season" },
];

/** Fixed mid-row card height on desktop — lists scroll inside; Season must not stretch the row. */
const MID_ROW_H = { xs: "auto", md: 320 };

/** Compact band presence — five swatches, not a bar. Tooltip carries counts. */
const BandSwatches = ({ bands }) => {
	const byKey = Object.fromEntries((bands || []).map((b) => [b.band, Number(b.total || 0)]));
	const tip = PRICE_BANDS.map((b) => `${b.label}: ${nf(byKey[b.key] || 0)}`).join(" · ");
	return (
		<Tooltip arrow title={tip || "No priced styles"}>
			<Stack direction="row" spacing={0.35} sx={{ cursor: "help" }}>
				{PRICE_BANDS.map((b) => {
					const n = byKey[b.key] || 0;
					return (
						<Box
							key={b.key}
							sx={{
								width: 9,
								height: 9,
								borderRadius: 0.4,
								background: n ? b.color : "#EEF0F3",
								outline: n ? "none" : `1px solid ${line}`,
							}}
						/>
					);
				})}
			</Stack>
		</Tooltip>
	);
};

const CompareRow = ({ row }) => (
	<Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.95, borderTop: `1px solid ${line}` }}>
		<Typography sx={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600, color: ink }} noWrap title={row.key}>
			{row.key}
		</Typography>
		<Typography sx={{ width: 36, fontSize: 11.5, color: ink, textAlign: "right", flexShrink: 0 }}>{nf(row.total)}</Typography>
		<Typography sx={{ width: 36, fontSize: 11.5, color: muted, textAlign: "right", flexShrink: 0 }}>
			{row.pricedRate}%
		</Typography>
		<Box sx={{ width: 56, flexShrink: 0, display: "flex", justifyContent: "center" }}>
			<BandSwatches bands={row.bands} />
		</Box>
		<Typography sx={{ width: 52, fontSize: 11.5, color: ink, textAlign: "right", flexShrink: 0 }}>
			{inr(row.medianMrp)}
		</Typography>
		<Typography
			sx={{
				width: 28,
				fontSize: 11.5,
				textAlign: "right",
				flexShrink: 0,
				color: row.blocked ? "#B4696B" : muted,
			}}>
			{nf(row.blocked)}
		</Typography>
	</Stack>
);

/** Compact ring + legend — style share and option share by MRP band. */
const PriceBandRing = ({ bands }) => {
	const raw = PRICE_BANDS.map((b) => {
		const match = (bands || []).find((row) => row.band === b.key || row.band === b.label);
		return {
			...b,
			total: Number(match?.total || 0),
			optionCount: Number(match?.optionCount || 0),
		};
	});
	const pricedTotal = raw.reduce((s, i) => s + i.total, 0);
	const optionTotal = raw.reduce((s, i) => s + i.optionCount, 0);
	// Always derive % from counts so a stale API without rate/optionRate still shows correctly.
	const pct = (part, whole) => (whole ? Number(((100 * part) / whole).toFixed(1)) : 0);
	const items = raw.map((i) => ({
		...i,
		rate: pct(i.total, pricedTotal),
		optionRate: pct(i.optionCount, optionTotal),
	}));
	let cursor = 0;
	const gradient =
		pricedTotal > 0
			? items
					.filter((i) => i.total > 0)
					.map((i) => {
						const start = cursor;
						cursor += (100 * i.total) / pricedTotal;
						return `${i.color} ${start}% ${cursor}%`;
					})
					.join(", ")
			: `${tone.neutral} 0% 100%`;

	return (
		<Box
			sx={{
				...card,
				width: { xs: "100%", md: 420 },
				height: MID_ROW_H,
				flexShrink: 0,
				boxSizing: "border-box",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}>
			<Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
				<Typography sx={{ ...eyebrow, flex: 1 }}>Price bands</Typography>
				<Tooltip
					arrow
					title="Priced styles (final_mrp > 0) by MRP band. Style % = share of priced styles. Options = colour rows (brand+category+style+colour+palette) on those styles; option % = share of all priced options.">
					<InfoOutlinedIcon sx={{ fontSize: 14, color: muted, cursor: "help" }} />
				</Tooltip>
			</Stack>

			<Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 1.5, flex: 1, minHeight: 0 }}>
				<Box
					sx={{
						width: 88,
						height: 88,
						borderRadius: "50%",
						background: `conic-gradient(${gradient})`,
						position: "relative",
						boxShadow: `inset 0 0 0 1px ${line}`,
						flexShrink: 0,
					}}>
					<Box
						sx={{
							position: "absolute",
							inset: 20,
							borderRadius: "50%",
							background: "#fff",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
						}}>
						<Typography sx={{ fontSize: 15, fontWeight: 600, color: ink, lineHeight: 1 }}>{nf(pricedTotal)}</Typography>
						<Typography sx={{ fontSize: 8.5, color: muted, letterSpacing: "0.06em", textTransform: "uppercase" }}>
							styles
						</Typography>
						<Typography sx={{ fontSize: 8.5, color: muted, mt: 0.15 }}>{nf(optionTotal)} opts</Typography>
					</Box>
				</Box>

				<Stack spacing={0.55} sx={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: "auto" }}>
					<Stack direction="row" spacing={0.75}>
						<Typography sx={{ ...colHead, flex: 1, minWidth: 0 }}>Band</Typography>
						<Typography sx={{ ...colHead, width: 58, textAlign: "right", flexShrink: 0 }}>Styles</Typography>
						<Typography sx={{ ...colHead, width: 58, textAlign: "right", flexShrink: 0 }}>Opts</Typography>
					</Stack>
					{items.map((i) => (
						<Tooltip
							key={i.key}
							arrow
							title={`${i.label}: ${nf(i.total)} styles (${i.rate}%) · ${nf(i.optionCount)} options (${i.optionRate}%)`}>
							<Stack direction="row" alignItems="center" spacing={0.75} sx={{ cursor: "help" }}>
								<Box sx={{ width: 8, height: 8, borderRadius: 0.4, background: i.color, flexShrink: 0 }} />
								<Typography sx={{ flex: 1, fontSize: 11, color: muted, minWidth: 0 }} noWrap>
									{i.label}
								</Typography>
								<Box sx={{ width: 58, flexShrink: 0, textAlign: "right" }}>
									<Typography sx={{ fontSize: 12, fontWeight: 600, color: i.total ? ink : muted, lineHeight: 1.1 }}>
										{nf(i.total)}
									</Typography>
									<Typography sx={{ fontSize: 9.5, color: muted, lineHeight: 1.1 }}>{i.rate}%</Typography>
								</Box>
								<Box sx={{ width: 58, flexShrink: 0, textAlign: "right" }}>
									<Typography
										sx={{ fontSize: 12, fontWeight: 600, color: i.optionCount ? ink : muted, lineHeight: 1.1 }}>
										{nf(i.optionCount)}
									</Typography>
									<Typography sx={{ fontSize: 9.5, color: muted, lineHeight: 1.1 }}>{i.optionRate}%</Typography>
								</Box>
							</Stack>
						</Tooltip>
					))}
				</Stack>
			</Stack>
		</Box>
	);
};

const ZeroMrpAlert = ({ alert }) => {
	if (!alert?.publishedAtZero) return null;
	const seasons = alert.bySeason || [];
	return (
		<Box
			sx={{
				border: "1px solid #E8C9C9",
				background: "#FDF6F6",
				borderRadius: 2.5,
				p: 2,
			}}>
			<Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-start" }}>
				<Box sx={{ flex: "0 1 280px", minWidth: 0 }}>
					<Typography sx={{ ...eyebrow, color: "#8F4A4C" }}>Data issue · published at ₹0</Typography>
					<Typography sx={{ fontSize: 22, fontWeight: 500, color: "#8F4A4C", mt: 0.5, lineHeight: 1.2 }}>
						{nf(alert.publishedAtZero)}
					</Typography>
					<Typography sx={{ fontSize: 12, color: "#8F4A4C", mt: 0.5, opacity: 0.85 }}>
						Published styles with final_mrp = 0 (invalid). Concentrated in legacy seasons —
						actionable escapes are the current-season rows below.
						{alert.pendingAtZero
							? ` ${nf(alert.pendingAtZero)} MRP_PENDING also at ₹0.`
							: null}
					</Typography>
				</Box>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Stack direction="row" spacing={1.5} sx={{ pb: 0.5 }}>
						<Typography sx={{ ...colHead, width: 72, flexShrink: 0 }}>Season</Typography>
						<Typography sx={{ ...colHead, width: 72, textAlign: "right", flexShrink: 0 }}>Published</Typography>
						<Typography sx={{ ...colHead, width: 64, textAlign: "right", flexShrink: 0 }}>At ₹0</Typography>
						<Typography sx={{ ...colHead, width: 48, textAlign: "right", flexShrink: 0 }}>%</Typography>
					</Stack>
					{(seasons.length ? seasons : []).slice(0, 8).map((r) => (
						<Stack key={r.season} direction="row" spacing={1.5} sx={{ py: 0.45 }}>
							<Typography sx={{ width: 72, fontSize: 12, color: ink, flexShrink: 0, fontWeight: 600 }} noWrap>
								{r.season}
							</Typography>
							<Typography sx={{ width: 72, fontSize: 12, color: ink, textAlign: "right", flexShrink: 0 }}>
								{nf(r.published)}
							</Typography>
							<Typography sx={{ width: 64, fontSize: 12, color: "#8F4A4C", textAlign: "right", flexShrink: 0 }}>
								{nf(r.atZero)}
							</Typography>
							<Typography sx={{ width: 48, fontSize: 12, color: muted, textAlign: "right", flexShrink: 0 }}>
								{r.atZeroRate}%
							</Typography>
						</Stack>
					))}
					{!seasons.length ? (
						<Typography sx={{ fontSize: 12, color: muted, py: 1 }}>No season breakdown.</Typography>
					) : null}
				</Box>
			</Stack>
		</Box>
	);
};

/** Compact multi-select for the panel's own scope. Empty selection reads as "All". */
const ScopeSelect = ({ label, options, value, onChange, disabled, compact = false }) => (
	<Select
		multiple
		displayEmpty
		size="small"
		disabled={disabled || !options.length}
		value={value}
		onChange={(e) => onChange(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
		renderValue={(selected) =>
			!selected.length
				? compact
					? label
					: `All ${label.toLowerCase()}s`
				: selected.length === 1
					? selected[0]
					: `${label}: ${selected.length}`
		}
		MenuProps={{ PaperProps: { style: { maxHeight: 280 } } }}
		sx={{
			minWidth: compact ? 88 : 132,
			maxWidth: compact ? 110 : undefined,
			fontSize: compact ? 11 : 12,
			background: "#fff",
			"& .MuiOutlinedInput-notchedOutline": { borderColor: line },
			"& .MuiSelect-select": { py: compact ? 0.35 : 0.6, color: value.length ? ink : muted },
		}}>
		{options.map((opt) => (
			<MenuItem key={opt} value={opt} dense>
				<Checkbox size="small" checked={value.indexOf(opt) > -1} sx={{ py: 0.25 }} />
				<ListItemText primaryTypographyProps={{ fontSize: compact ? 11.5 : 12.5 }} primary={opt} />
			</MenuItem>
		))}
	</Select>
);

const EMPTY_FILTER = Object.freeze({});

const MrpAnalytics = ({ filter = EMPTY_FILTER, search = "", onMoreFilters, activeFilterCount = 0 }) => {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [open, setOpen] = useState(true);
	const [dimension, setDimension] = useState("brand");

	/**
	 * Option lists for the category-card pickers. Captured once from an unscoped response so
	 * applying More Filters does not shrink Brand/Division/Season menus.
	 */
	const [scopeOptions, setScopeOptions] = useState({ season: [], brand: [], division: [] });
	const scopeOptionsReady = useRef(false);

	/** Category-card-only filters — layered on top of panel/table More Filters. */
	const [catScope, setCatScope] = useState({ brand: [], division: [], season: [] });
	const [catRows, setCatRows] = useState(null);
	const [catLoading, setCatLoading] = useState(false);

	const effectiveFilter = useMemo(() => ({ ...(filter || EMPTY_FILTER) }), [filter]);
	const filterKey = useMemo(() => JSON.stringify(effectiveFilter || {}), [effectiveFilter]);
	const panelFilterEmpty = useMemo(() => {
		const keys = Object.keys(effectiveFilter || {}).filter((k) => {
			const v = effectiveFilter[k];
			if (v == null || v === "") return false;
			if (Array.isArray(v)) return v.length > 0;
			return true;
		});
		return keys.length === 0 && !String(search || "").trim();
	}, [effectiveFilter, search]);

	const isCatScoped = Boolean(catScope.brand.length || catScope.division.length || catScope.season.length);

	const catFilter = useMemo(() => {
		const next = { ...effectiveFilter };
		for (const key of ["brand", "division", "season"]) {
			if (catScope[key]?.length) next[key] = catScope[key];
		}
		return next;
	}, [effectiveFilter, catScope]);
	const catFilterKey = useMemo(() => JSON.stringify(catFilter || {}), [catFilter]);

	const fetchSummary = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const { data: res } = await axios.post(`${MRP_SHEET_API}/analytics/summary`, {
				filter: effectiveFilter,
				search,
			});
			const result = res?.result || null;
			setData(result);
			if (result && panelFilterEmpty && !scopeOptionsReady.current) {
				scopeOptionsReady.current = true;
				setScopeOptions({
					season: (result.groups?.season?.rows || []).map((r) => r.key),
					brand: (result.groups?.brand?.rows || []).map((r) => r.key),
					division: result.filterOptions?.division || [],
				});
			}
		} catch (err) {
			setError(parseErrorMessage(err));
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterKey, search]);

	useEffect(() => {
		fetchSummary();
	}, [fetchSummary]);

	/** Category table: use panel data when card filters empty; else refetch with cat scope. */
	useEffect(() => {
		let cancelled = false;
		const run = async () => {
			if (!isCatScoped) {
				setCatRows((prev) => (prev == null ? prev : null));
				setCatLoading(false);
				return;
			}
			setCatLoading(true);
			try {
				const { data: res } = await axios.post(`${MRP_SHEET_API}/analytics/summary`, {
					filter: catFilter,
					search,
				});
				if (!cancelled) setCatRows(res?.result?.price?.byCategory || []);
			} catch {
				if (!cancelled) setCatRows([]);
			} finally {
				if (!cancelled) setCatLoading(false);
			}
		};
		run();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCatScoped, catFilterKey, search]);

	const { applied, ignored } = useMemo(() => splitAnalyticsFilters(effectiveFilter), [effectiveFilter]);

	const group = data?.groups?.[dimension];
	const rows = useMemo(() => group?.rows || [], [group]);

	const coverage = data?.coverage;
	const readiness = data?.readiness;
	const price = data?.price;
	const blockedTotal = (readiness?.blockedMrp || 0) + (readiness?.blockedSizes || 0);
	const categoryRows = isCatScoped ? catRows || [] : price?.byCategory || [];

	return (
		<Box sx={{ background: panelBg, border: `1px solid ${line}`, borderRadius: 3, p: 2.5, mb: 2 }}>
			<Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
				<Typography sx={{ fontSize: 14.5, fontWeight: 600, color: ink, flexShrink: 0 }}>MRP analytics</Typography>
				<Box sx={{ minWidth: 0, flex: "1 1 auto", overflow: "hidden" }}>
					<ScopeChips applied={applied} ignored={ignored} search={search} emptyLabel="all styles" />
				</Box>
				{typeof onMoreFilters === "function" ? (
					<Button
						size="small"
						variant="outlined"
						startIcon={<FilterAltRounded sx={{ fontSize: 16 }} />}
						onClick={onMoreFilters}
						sx={{
							textTransform: "none",
							fontSize: 12,
							color: muted,
							borderColor: activeFilterCount ? ink : line,
							bgcolor: activeFilterCount ? "#fff" : "transparent",
							borderRadius: 1,
							minHeight: 30,
							fontWeight: activeFilterCount ? 600 : 500,
							flexShrink: 0,
						}}>
						More Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
					</Button>
				) : null}
				<Tooltip title="Refresh" arrow>
					<span>
						<IconButton size="small" onClick={fetchSummary} disabled={loading}>
							{loading ? (
								<CircularProgress size={14} sx={{ color: muted }} />
							) : (
								<RefreshRounded sx={{ fontSize: 17, color: muted }} />
							)}
						</IconButton>
					</span>
				</Tooltip>
				<IconButton size="small" onClick={() => setOpen((v) => !v)} aria-label={open ? "Collapse" : "Expand"}>
					{open ? (
						<ExpandLessRounded sx={{ fontSize: 19, color: muted }} />
					) : (
						<ExpandMoreRounded sx={{ fontSize: 19, color: muted }} />
					)}
				</IconButton>
			</Stack>

			<Collapse in={open}>
				{error ? (
					<Typography sx={{ fontSize: 13, color: "#B4696B", mt: 2 }}>{error}</Typography>
				) : !data && loading ? (
					<Stack alignItems="center" sx={{ py: 5 }}>
						<CircularProgress size={22} sx={{ color: tone.mrpPending }} />
					</Stack>
				) : data ? (
					<Stack spacing={2} sx={{ mt: 2 }}>
						<ZeroMrpAlert alert={data.alert} />

						<Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="stretch">
							<Stack
								direction="row"
								spacing={1.5}
								flexWrap="wrap"
								useFlexGap
								sx={{
									width: { xs: "100%", md: "50%" },
									flexShrink: 0,
									boxSizing: "border-box",
									"& > *": { flex: "1 1 0", minWidth: 120 },
								}}>
								<KpiTile
									label="Priced coverage"
									value={`${coverage?.pricedRate ?? 0}%`}
									hint={`${nf(coverage?.priced)} of ${nf(coverage?.total)} · final_mrp > 0`}
									accent={tone.published}
									info="Share of styles in the current scope with final_mrp > 0. Unscoped coverage looks low because legacy seasons were priced outside Relook — filter by season to see live work."
								/>
								<KpiTile
									label="Ready to publish"
									value={nf(readiness?.ready)}
									hint={`of ${nf(readiness?.pending)} MRP_PENDING`}
									accent={tone.designerPending}
									info="MRP_PENDING styles that have a valid Final MRP (final_mrp > 0) and non-empty sizes — the set this panel considers publish-ready."
								/>
								<KpiTile
									label="Blocked"
									value={nf(blockedTotal)}
									hint={`${nf(readiness?.blockedMrp)} on MRP (guard fix pending) · ${nf(readiness?.blockedSizes)} on sizes`}
									accent={tone.rejected}
									info="MRP_PENDING styles that cannot publish cleanly: missing/zero Final MRP (treated as invalid here — Publish still allows ₹0 until the guard is fixed) or priced but missing sizes."
								/>
								<KpiTile
									label="Avg MRP"
									value={inr(price?.meanMrp)}
									hint={
										price?.medianMrp != null ? `Median MRP ${inr(price.medianMrp)}` : "Median MRP —"
									}
									accent={tone.mrpPending}
									info="Both over priced styles only (final_mrp > 0). Avg is the arithmetic mean; Median is the middle value so outliers drag the average less. Null/zero MRP styles are excluded."
								/>
							</Stack>

							{/* Right half — Avg COGS + Multiple; room for more tiles later */}
							<Stack
								direction="row"
								spacing={1.5}
								flexWrap="wrap"
								useFlexGap
								sx={{
									flex: 1,
									minWidth: 0,
									boxSizing: "border-box",
									"& > *": { flex: "1 1 0", minWidth: 140, maxWidth: 220 },
								}}>
								<KpiTile
									label="Avg COGS"
									value={inr(data.margin?.meanCogs)}
									hint={
										data.margin?.medianMultiple != null
											? `Multiple ${Number(data.margin.medianMultiple).toFixed(1)}×`
											: "Multiple —"
									}
									accent={tone.designerPending}
									info={`Average colour COGS where cogs > 0 (n=${nf(data.margin?.cogsSample || 0)}). Multiple is NOT avg(MRP)÷avg(COGS). For each non-rejected colour in scope we compute style final_mrp ÷ that colour's cogs, keep ratios in 1.5×–10×, then take the median of those ratios (n=${nf(data.margin?.multipleSample || 0)}).`}
								/>
							</Stack>
						</Stack>

						<Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="stretch">
							<Box
								sx={{
									...card,
									width: { xs: "100%", md: "25%" },
									minWidth: { md: 280 },
									maxWidth: { md: 360 },
									height: MID_ROW_H,
									flexShrink: 0,
									boxSizing: "border-box",
									display: "flex",
									flexDirection: "column",
									overflow: "hidden",
								}}>
								<Stack
									direction="row"
									alignItems="center"
									spacing={1}
									flexWrap="wrap"
									useFlexGap
									sx={{ flexShrink: 0 }}>
									<Typography sx={eyebrow}>Compare by</Typography>
									<DimensionToggle
										value={dimension}
										onChange={setDimension}
										options={DIMENSIONS}
										selectedTone={tone.mrpPending}
									/>
								</Stack>

								<Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5, pb: 0.5, flexShrink: 0 }}>
									<Typography sx={{ ...colHead, flex: 1, minWidth: 0 }}>
										{dimension === "brand" ? "Brand" : "Season"}
									</Typography>
									<Typography sx={{ ...colHead, width: 36, textAlign: "right", flexShrink: 0 }}>N</Typography>
									<Typography sx={{ ...colHead, width: 36, textAlign: "right", flexShrink: 0 }}>%</Typography>
									<Typography sx={{ ...colHead, width: 56, textAlign: "center", flexShrink: 0 }}>Bands</Typography>
									<Typography sx={{ ...colHead, width: 52, textAlign: "right", flexShrink: 0 }}>Med</Typography>
									<Typography sx={{ ...colHead, width: 28, textAlign: "right", flexShrink: 0 }}>Blk</Typography>
								</Stack>

								<Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
									{rows.length ? (
										rows.map((row) => <CompareRow key={row.key} row={row} />)
									) : (
										<Typography sx={{ fontSize: 12, color: muted, py: 1.5 }}>No styles to compare.</Typography>
									)}
								</Box>

								<Typography sx={{ fontSize: 10.5, color: muted, mt: 1, lineHeight: 1.45, flexShrink: 0 }}>
									Hover bands for counts. Priced only. Blk = blocked on MRP (guard fix pending).
									{group?.truncated ? (
										<Box component="span" sx={{ color: "#8A6A2F", fontWeight: 600 }}>
											{" "}
											Top {rows.length} only.
										</Box>
									) : null}
								</Typography>
							</Box>

							<PriceBandRing bands={price?.bands || []} />

							<Box
								sx={{
									...card,
									flex: 1,
									minWidth: { xs: "100%", md: 280 },
									height: MID_ROW_H,
									boxSizing: "border-box",
									display: "flex",
									flexDirection: "column",
									overflow: "hidden",
								}}>
								<Stack
									direction="row"
									alignItems="center"
									spacing={0.75}
									flexWrap="wrap"
									useFlexGap
									sx={{ flexShrink: 0, mb: 0.5 }}>
									<Typography sx={{ ...eyebrow, mr: 0.5 }}>MRP by category</Typography>
									<ScopeSelect
										compact
										label="Brand"
										options={scopeOptions.brand}
										value={catScope.brand}
										onChange={(v) => setCatScope((s) => ({ ...s, brand: v }))}
										disabled={loading && !data}
									/>
									<ScopeSelect
										compact
										label="Division"
										options={scopeOptions.division}
										value={catScope.division}
										onChange={(v) => setCatScope((s) => ({ ...s, division: v }))}
										disabled={loading && !data}
									/>
									<ScopeSelect
										compact
										label="Season"
										options={scopeOptions.season}
										value={catScope.season}
										onChange={(v) => setCatScope((s) => ({ ...s, season: v }))}
										disabled={loading && !data}
									/>
									{isCatScoped ? (
										<Button
											size="small"
											onClick={() => setCatScope({ brand: [], division: [], season: [] })}
											sx={{ fontSize: 10.5, textTransform: "none", color: muted, minWidth: 0, px: 0.75 }}>
											Clear
										</Button>
									) : null}
									{catLoading ? <CircularProgress size={12} sx={{ color: muted }} /> : null}
								</Stack>
								<Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.5, pb: 0.5, flexShrink: 0 }}>
									<Typography sx={{ ...colHead, flex: 1, minWidth: 0 }}>Category</Typography>
									<Typography sx={{ ...colHead, width: 32, textAlign: "right", flexShrink: 0 }}>N</Typography>
									<Typography sx={{ ...colHead, width: 36, textAlign: "right", flexShrink: 0 }}>Opts</Typography>
									<Typography sx={{ ...colHead, width: 64, textAlign: "right", flexShrink: 0 }}>MRP(AVG)</Typography>
									<Typography sx={{ ...colHead, width: 52, textAlign: "right", flexShrink: 0 }}>COGS</Typography>
									<Typography sx={{ ...colHead, width: 72, textAlign: "right", flexShrink: 0 }}>Range</Typography>
									<Typography sx={{ ...colHead, width: 40, textAlign: "right", flexShrink: 0 }}>Mult</Typography>
								</Stack>
								<Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
									{categoryRows.length ? (
										categoryRows.map((r) => (
											<Stack
												key={r.category}
												direction="row"
												alignItems="center"
												spacing={0.75}
												sx={{ py: 0.7, borderTop: `1px solid ${line}` }}>
												<Typography
													sx={{ flex: 1, minWidth: 0, fontSize: 11.5, fontWeight: 600, color: ink }}
													noWrap
													title={r.category}>
													{r.category}
												</Typography>
												<Typography sx={{ width: 32, fontSize: 11.5, color: ink, textAlign: "right", flexShrink: 0 }}>
													{nf(r.priced)}
												</Typography>
												<Typography sx={{ width: 36, fontSize: 11.5, color: ink, textAlign: "right", flexShrink: 0 }}>
													{nf(r.optionCount)}
												</Typography>
												<Typography sx={{ width: 64, fontSize: 11.5, color: ink, textAlign: "right", flexShrink: 0 }}>
													{inr(r.meanMrp ?? r.medianMrp)}
												</Typography>
												<Typography sx={{ width: 52, fontSize: 11.5, color: ink, textAlign: "right", flexShrink: 0 }}>
													{inr(r.meanCogs)}
												</Typography>
												<Typography sx={{ width: 72, fontSize: 10.5, color: muted, textAlign: "right", flexShrink: 0 }} noWrap>
													{r.minMrp != null && r.maxMrp != null ? `${inr(r.minMrp)}–${inr(r.maxMrp)}` : "—"}
												</Typography>
												<Tooltip
													arrow
													title={
														r.medianMultiple != null
															? `Median style MRP ÷ colour COGS over ${nf(r.multipleSample)} options (cogs > 0)`
															: r.meanCogs
																? "No colour-level MRP÷COGS sample"
																: "No colour COGS > 0 in this category"
													}>
													<Typography
														sx={{ width: 40, fontSize: 11.5, color: ink, textAlign: "right", flexShrink: 0 }}>
														{r.medianMultiple != null ? `${Number(r.medianMultiple).toFixed(1)}×` : "—"}
													</Typography>
												</Tooltip>
											</Stack>
										))
									) : (
										<Typography sx={{ fontSize: 12, color: muted, py: 1.5 }}>
											{catLoading
												? "Loading…"
												: "No categories with priced styles in this scope."}
										</Typography>
									)}
								</Box>
								<Typography sx={{ fontSize: 9.5, color: muted, mt: 1, lineHeight: 1.35, flexShrink: 0 }}>
									Priced only. MRP(AVG) = mean MRP. COGS = mean colour cogs &gt; 0. Mult = median MRP÷COGS
									(needs colour COGS). Card filters apply here only.
								</Typography>
							</Box>

							<Box
								sx={{
									...card,
									width: { xs: "100%", md: 168 },
									height: MID_ROW_H,
									flexShrink: 0,
									boxSizing: "border-box",
									display: "flex",
									flexDirection: "column",
									overflow: "hidden",
								}}>
								<Typography sx={{ ...eyebrow, flexShrink: 0 }}>MRP_PENDING ageing</Typography>
								<Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
									<MiniBars
										dense
										rows={readiness?.aging || []}
										labelKey="bucket"
										valueKey="total"
										color={tone.mrpPending}
									/>
								</Box>
								<Typography sx={{ fontSize: 9.5, color: muted, mt: 1, lineHeight: 1.35, flexShrink: 0 }}>
									Actual days post sourcing stage. Uses sourcing close date when present; otherwise style
									created date.
								</Typography>
							</Box>
						</Stack>
					</Stack>
				) : null}
			</Collapse>
		</Box>
	);
};

export default MrpAnalytics;
